/**
 * SuggestionsLayer
 *
 * Positions up to three ghost candidates after the last chord, driven by the
 * ghosts engine (src/lib/ghosts, suggestNextChords). Ghosts render when not in
 * voice-lane mode and the progression is non-empty (the parent gates on that).
 * Hover auditions a candidate (no commit); click commits it at the next beat.
 * The on/off flag lives in the suggestion store so ghosts can be turned off.
 */
import React, { useCallback, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { Chord, MusicalKey, Mode } from '@/types/chord';
import { suggestNextChords, type GhostChordPayload } from '@/lib/ghosts';
import { useSuggestionStore } from '@/store/suggestion-store';
import { GhostChordSuggestion } from './GhostChordSuggestion';
import { CANVAS_CONFIG, DEFAULT_CHORD_SIZE } from '@/utils/constants';
import styles from './SuggestionsLayer.module.css';

interface SuggestionsLayerProps {
  chords: Chord[];
  musicKey: MusicalKey;
  mode: Mode;
  zoom: number;
  /** Hover: voice + play once, never commit. */
  onAuditionGhost: (payload: GhostChordPayload) => void;
  /** Click: append the candidate at the next beat. */
  onCommitGhost: (payload: GhostChordPayload) => void;
}

const SuggestionsLayerComponent: React.FC<SuggestionsLayerProps> = ({
  chords,
  musicKey,
  mode,
  zoom,
  onAuditionGhost,
  onCommitGhost,
}) => {
  const hoveredIndex = useSuggestionStore((state) => state.hoveredIndex);
  const setHovered = useSuggestionStore((state) => state.setHovered);
  const isEnabled = useSuggestionStore((state) => state.isEnabled);

  const candidates = useMemo(
    () => suggestNextChords({ chords, key: musicKey, mode }),
    [chords, musicKey, mode]
  );

  // The last chord anchors placement; ghosts sit in the beat slots after it.
  const lastChord = useMemo(() => {
    if (chords.length === 0) return null;
    return [...chords].sort((a, b) => a.startBeat - b.startBeat)[chords.length - 1];
  }, [chords]);

  // Stagger the (up to three) candidates so they fit side by side after the last chord.
  const positions = useMemo(() => {
    const beatWidth = CANVAS_CONFIG.GRID_BEAT_WIDTH * zoom;
    const startBeat = lastChord ? lastChord.startBeat + lastChord.duration : 0;
    const gap = 0.5;
    const chordWidth = DEFAULT_CHORD_SIZE * zoom;
    return candidates.map((_, index) => ({
      x: (startBeat + index * (1 + gap)) * beatWidth + chordWidth / 2,
    }));
  }, [candidates, lastChord, zoom]);

  const handleHover = useCallback(
    (index: number, payload: GhostChordPayload) => {
      setHovered(index);
      onAuditionGhost(payload);
    },
    [setHovered, onAuditionGhost]
  );

  if (!isEnabled || candidates.length === 0) {
    return null;
  }

  return (
    <div className={styles.suggestionsLayer}>
      <AnimatePresence mode="popLayout">
        {candidates.map((candidate, index) => {
          const position = positions[index];
          if (!position) return null;

          return (
            <div
              key={`ghost-${candidate.kind}-${candidate.chord.scaleDegree}-${index}`}
              className={styles.suggestionPosition}
              style={{ left: position.x, bottom: 0 }}
            >
              <GhostChordSuggestion
                candidate={candidate}
                index={index}
                zoom={zoom}
                isHovered={hoveredIndex === index}
                onHover={() => handleHover(index, candidate.chord)}
                onLeave={() => setHovered(null)}
                onClick={() => onCommitGhost(candidate.chord)}
              />
            </div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

SuggestionsLayerComponent.displayName = 'SuggestionsLayer';

export const SuggestionsLayer = React.memo(SuggestionsLayerComponent);
